import { ArrowRight, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { onboardingSetupCtaLabel } from '@/lib/onboarding-display'

export type OnboardingWelcomePanelVariant = 'card' | 'page'

export interface OnboardingWelcomePanelProps {
  title: string
  description?: string
  tagline?: string
  completed: number
  total: number
  percent: number
  onContinue: () => void
  isContinuing?: boolean
  variant?: OnboardingWelcomePanelVariant
  className?: string
  coPilotHint?: string
  onAskAgent?: () => void
  askAgentLabel?: string
}

export function OnboardingWelcomePanel({
  title,
  description,
  tagline,
  completed,
  total,
  percent,
  onContinue,
  isContinuing = false,
  variant = 'card',
  className,
  coPilotHint,
  onAskAgent,
  askAgentLabel = 'Ask the agent',
}: OnboardingWelcomePanelProps) {
  const isPage = variant === 'page'

  return (
    <div
      className={cn(
        'flex flex-col',
        isPage
          ? 'mx-auto w-full max-w-lg gap-10 py-8 text-center'
          : 'gap-5',
        className,
      )}
    >
      {description ? (
        <p
          className={cn(
            'leading-relaxed text-muted-foreground',
            isPage ? 'text-base' : 'text-sm',
          )}
        >
          {description}
        </p>
      ) : null}

      {coPilotHint ? (
        <p
          className={cn(
            'leading-relaxed text-muted-foreground',
            isPage ? 'text-sm' : 'text-xs',
          )}
        >
          {coPilotHint}
        </p>
      ) : null}

      <div className={cn(isPage ? 'space-y-3' : 'space-y-1.5')}>
        <h2
          className={cn(
            'font-bold tracking-tight text-foreground',
            isPage ? 'text-4xl' : 'text-2xl',
          )}
        >
          {title}
        </h2>
        {tagline ? (
          <p
            className={cn(
              'leading-relaxed text-muted-foreground',
              isPage ? 'text-xl' : 'text-sm',
            )}
          >
            {tagline}
          </p>
        ) : null}
      </div>

      <div className={cn('space-y-2', isPage && 'px-1')}>
        <Progress value={percent} aria-label="Onboarding progress" />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {completed} of {total} steps completed
          </span>
          <span className="tabular-nums">{percent}%</span>
        </div>
      </div>

      <div
        className={cn(
          'flex flex-wrap items-center gap-2',
          isPage ? 'justify-center pt-2' : 'pt-1',
        )}
      >
        <Button
          onClick={onContinue}
          disabled={isContinuing}
          className={cn('rounded-lg', isPage ? 'px-8 py-2.5 text-base' : 'px-5')}
        >
          {isContinuing ? 'Continuing…' : onboardingSetupCtaLabel(completed)}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
        {onAskAgent ? (
          <Button
            type="button"
            variant="outline"
            onClick={onAskAgent}
            disabled={isContinuing}
            className={cn('rounded-lg', isPage ? 'px-6 py-2.5 text-base' : 'px-4')}
          >
            <MessageSquare className="mr-1.5 h-4 w-4" />
            {askAgentLabel}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
